import React, { useState } from 'react';
import { X, Phone, Mail, Calendar, FileText, CheckSquare } from 'lucide-react';
import { createActivity, type ActivityType } from '../lib/activityService';

interface ActivityFormProps {
  opportunityId: string;
  brandId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const activityTypes: { value: ActivityType; label: string; icon: any }[] = [
  { value: 'call', label: 'Phone Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'meeting', label: 'Meeting', icon: Calendar },
  { value: 'note', label: 'Note', icon: FileText },
  { value: 'task', label: 'Task', icon: CheckSquare },
];

export default function ActivityForm({ opportunityId, brandId, onClose, onSuccess }: ActivityFormProps) {
  const [activityType, setActivityType] = useState<ActivityType>('note');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [activityDate, setActivityDate] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [outcome, setOutcome] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setSaving(true);

    try {
      const activityData: any = {
        opportunity_id: opportunityId,
        brand_id: brandId,
        activity_type: activityType,
        title: title.trim(),
        description: description.trim() || undefined,
        outcome: outcome.trim() || undefined,
        is_completed: isCompleted,
      };

      if (activityDate) {
        activityData.activity_date = new Date(activityDate).toISOString();
      }

      if (durationMinutes && !isNaN(Number(durationMinutes))) {
        activityData.duration_minutes = Number(durationMinutes);
      }

      if (scheduledAt) {
        activityData.scheduled_at = new Date(scheduledAt).toISOString();
      }

      const result = await createActivity(activityData);

      if (result) {
        onSuccess();
        onClose();
      } else {
        setError('Failed to create activity');
      }
    } catch (err) {
      console.error('Error creating activity:', err);
      setError('An error occurred while creating the activity');
    } finally {
      setSaving(false);
    }
  };

  const showDuration = activityType === 'call' || activityType === 'meeting';
  const showScheduled = activityType === 'task' || activityType === 'meeting';
  const showCompleted = activityType === 'task';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">Log Activity</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Activity Type</label>
            <div className="grid grid-cols-5 gap-3">
              {activityTypes.map((type) => {
                const Icon = type.icon;
                const isSelected = activityType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setActivityType(type.value)}
                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center space-y-2 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <Icon className={`w-6 h-6 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                    <span
                      className={`text-xs font-medium ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}
                    >
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`Enter ${activityType} title`}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label htmlFor="activityDate" className="block text-sm font-medium text-gray-700 mb-2">
              Activity Date & Time *
            </label>
            <input
              type="datetime-local"
              id="activityDate"
              value={activityDate}
              onChange={(e) => setActivityDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">When did this activity occur?</p>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details, notes, or context"
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {showDuration && (
              <div>
                <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-2">
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  id="duration"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                  placeholder="30"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}

            {showScheduled && (
              <div className={showDuration ? '' : 'col-span-2'}>
                <label htmlFor="scheduled" className="block text-sm font-medium text-gray-700 mb-2">
                  {activityType === 'task' ? 'Due Date' : 'Scheduled For'}
                </label>
                <input
                  type="datetime-local"
                  id="scheduled"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="outcome" className="block text-sm font-medium text-gray-700 mb-2">
              Outcome
            </label>
            <textarea
              id="outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              placeholder="What was the result or next step?"
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {showCompleted && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="completed"
                checked={isCompleted}
                onChange={(e) => setIsCompleted(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="completed" className="ml-2 text-sm text-gray-700">
                Mark as completed
              </label>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <span>Log Activity</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
